/**
 * ProblemasDaEntregaSection — os problemas da entrega na Conferência (Contract-Driven,
 * Onda 3 — SPEC-ONDA-3 FR-15).
 *
 * Cada item é um gap do repair-manifest: o que falhou, quem corrige (dono = camada e papel
 * do squad), a ação (corrigir / decidir / conferir), o requisito ligado e a evidência.
 * Filtrável por dono; agrupável por dono ou por requisito (RF-nn). Os mesmos itens saem em
 * CORRECOES-PENDENTES.md e ATIVIDADES-COMPLEMENTARES.md da entrega.
 *
 * `manifest` null/undefined → não renderiza nada.
 */
import { useMemo, useState } from 'react';
import { C } from '@/theme/palette';
import { useExpandirTudo } from '@/components/ExportacaoContext';

export const DONOS = {
  dados: ['Dados', 'data-engineer'],
  testes_integracao: ['Suíte de integração', 'test-automator'],
  servicos: ['Serviços', 'backend-developer'],
  telas_web: ['Telas web', 'frontend-developer'],
  telas_mobile: ['Telas mobile', 'mobile-developer'],
  docs: ['Documentação', 'documentation-engineer'],
  infra_cloud: ['Infra', 'devops-engineer'],
};
export const ORDEM_DONOS = Object.keys(DONOS);
export const ACAO = { AUTO_REPAIR: 'corrigir', REQUIRES_DECISION: 'decidir', INFORMAR: 'conferir' };
const COR_ACAO = { AUTO_REPAIR: C.hot, REQUIRES_DECISION: C.warn, INFORMAR: C.neutralDim };

export function rotuloDoDono(dono) {
  const [nome, papel] = DONOS[dono] ?? [dono, '?'];
  return `${nome} · ${papel}`;
}

function Chip({ ativo, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      data-export="estatico"
      style={{
        fontSize: 11.5, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
        backgroundColor: ativo ? `${C.cyber}22` : C.blackElev,
        border: `1px solid ${ativo ? C.cyber : C.blackBorder}`,
        color: ativo ? C.cyber : C.neutralDim,
      }}
    >
      {children}
    </button>
  );
}

function Item({ g }) {
  return (
    <li style={{
      padding: '10px 12px', borderRadius: 8, backgroundColor: C.blackElev,
      border: `1px solid ${g.severity === 'bloqueio' ? `${C.hot}55` : C.blackBorder}`,
    }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: COR_ACAO[g.action] ?? C.neutralDim,
                       textTransform: 'uppercase' }}>
          {ACAO[g.action] ?? g.action}
        </span>
        <span style={{ fontSize: 11, color: C.neutralMuted, fontFamily: 'monospace' }}>
          {g.category}
        </span>
        <span style={{ fontSize: 11, color: C.neutralDim }}>{rotuloDoDono(g.ownerAgent)}</span>
        {g.sourceRequirement && (
          <span style={{ fontSize: 11, color: C.cyber, fontFamily: 'monospace' }}>
            {g.sourceRequirement}
          </span>
        )}
        {g.severity === 'aviso' && (
          <span style={{ fontSize: 10.5, color: C.neutralMuted }}>aviso</span>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: C.neutral, marginTop: 4 }}>{g.message}</div>
      {(g.evidence ?? []).map((ev) => (
        <div key={ev} style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2 }}>
          Evidência: {ev}
        </div>
      ))}
      {g.log && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ fontSize: 11, color: C.neutralDim, cursor: 'pointer' }}>
            Log da subida da stack
          </summary>
          <pre style={{ fontSize: 10.5, color: C.neutralMuted, whiteSpace: 'pre-wrap',
                        maxHeight: 280, overflow: 'auto', margin: '6px 0 0' }}>{g.log}</pre>
        </details>
      )}
    </li>
  );
}

export function ProblemasDaEntregaSection({ manifest }) {
  const [escolhaDono, setDono] = useState(null);
  // Exportação: todos os donos (a escolha do usuário fica guardada).
  const expandirTudo = useExpandirTudo();
  const dono = expandirTudo ? null : escolhaDono;
  const [agrupar, setAgrupar] = useState('dono');

  const itens = useMemo(
    () => (manifest?.itens ?? []).filter((g) => !dono || g.ownerAgent === dono),
    [manifest, dono],
  );

  const grupos = useMemo(() => {
    const out = new Map();
    for (const g of itens) {
      const chave = agrupar === 'dono'
        ? rotuloDoDono(g.ownerAgent)
        : (g.sourceRequirement || 'Sem requisito ligado');
      if (!out.has(chave)) out.set(chave, []);
      out.get(chave).push(g);
    }
    const ordem = agrupar === 'dono'
      ? (a, b) => ORDEM_DONOS.indexOf(a[1][0].ownerAgent) - ORDEM_DONOS.indexOf(b[1][0].ownerAgent)
      : (a, b) => (a[0] === 'Sem requisito ligado') - (b[0] === 'Sem requisito ligado')
        || a[0].localeCompare(b[0], 'pt-BR');
    return [...out.entries()].sort(ordem);
  }, [itens, agrupar]);

  if (!manifest) return null;
  const total = manifest.itens?.length ?? 0;

  return (
    <section aria-label="Problemas da entrega" style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: C.neutral, margin: '0 0 4px' }}>
        Problemas da entrega ({total.toLocaleString('pt-BR')})
      </h2>
      <p style={{ fontSize: 12, color: C.neutralDim, margin: '0 0 12px' }}>
        Cada item diz quem corrige, o que fazer e a qual requisito se liga — os mesmos itens
        de CORRECOES-PENDENTES.md e ATIVIDADES-COMPLEMENTARES.md da entrega.
      </p>

      {total === 0 && (
        <p style={{ fontSize: 12, color: C.neutral, margin: '0 0 12px' }}>
          Nenhum problema aberto na entrega mais recente.
        </p>
      )}
      {total > 0 && (<>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <Chip ativo={!dono} onClick={() => setDono(null)}>Todos ({total})</Chip>
        {ORDEM_DONOS.filter((d) => manifest.porDono?.[d]).map((d) => (
          <Chip key={d} ativo={dono === d} onClick={() => setDono(d === dono ? null : d)}>
            {DONOS[d][0]} ({manifest.porDono[d]})
          </Chip>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <Chip ativo={agrupar === 'dono'} onClick={() => setAgrupar('dono')}>Agrupar por dono</Chip>
        <Chip ativo={agrupar === 'requisito'} onClick={() => setAgrupar('requisito')}>
          Agrupar por requisito
        </Chip>
      </div>
      </>)}

      {grupos.map(([chave, lista]) => (
        <div key={chave} style={{ marginBottom: 14 }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: C.neutralMuted, margin: '0 0 6px' }}>
            {chave} ({lista.length})
          </h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {lista.map((g) => <Item key={`${g.repo}:${g.id}`} g={g} />)}
          </ul>
        </div>
      ))}
    </section>
  );
}
